// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { contractsApi } from '../services/api';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  Tag, 
  Dropdown, 
  message,
  Tooltip
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  FilterOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Title } from '../components/design-system/typography';
import { CardContainer } from '../components/contract/CardContainer';
import { StatusPill } from '../components/design-system/StatusPill';
import { colors, spacing } from '../components/design-system/tokens';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ContractListItem {
  id: string;
  reference: string;
  supplier: {
    name: string;
    shortName: string | null;
  };
  buyer: {
    name: string;
    shortName: string | null;
  };
  status: 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  contractDate: string;
  cropYear: string;
  commissionRate: number | null;
  createdAt: string;
  updatedAt: string;
  _count: {
    shipments: number;
  };
}

// API response interface
interface ApiResponse {
  contracts: Array<{
    id: string;
    reference: string;
    supplier: {
      name: string;
      shortName: string | null;
    };
    buyer: {
      name: string;
      shortName: string | null;
    };
    status: string;
    contractDate?: string;
    cropYear?: string;
    commissionRate?: number | null;
    createdAt?: string;
    updatedAt?: string;
    _count?: {
      shipments: number;
    };
    // Legacy format support
    contract_date?: string;
    crop_year?: string;
    commission_rate?: number | null;
    created_at?: string;
    updated_at?: string;
    shipments_count?: number;
  }>;
}

// API client function with search/filter parameters
const fetchContracts = async (filters: {
  search?: string;
  status?: string;
  supplier?: string;
  buyer?: string;
  year?: string;
  cropYear?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
} = {}): Promise<ContractListItem[]> => {
  try {
    // Map frontend parameters to backend parameter names
    const apiParams: any = {
      limit: filters.limit || 50,
      search: filters.search,
      status: filters.status,
    };
    
    // Map year filter
    if (filters.year) {
      apiParams.year = filters.year;
    }
    
    // Map cropYear to crop_year for backend compatibility
    if (filters.cropYear) {
      apiParams.crop_year = filters.cropYear;
    }
    
    // Map date filters
    if (filters.dateFrom) {
      apiParams.contract_date_from = filters.dateFrom;
    }
    if (filters.dateTo) {
      apiParams.contract_date_to = filters.dateTo;
    }
    
    const data: ApiResponse = await contractsApi.getContracts(apiParams);
    
    // Transform API response to match our interface
    return data.contracts.map(contract => ({
    id: contract.id,
    reference: contract.reference,
    supplier: {
      name: contract.supplier.name,
      shortName: contract.supplier.shortName,
    },
    buyer: {
      name: contract.buyer.name,
      shortName: contract.buyer.shortName,
    },
    status: contract.status.toUpperCase() as 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED',
    contractDate: contract.contract_date || contract.contractDate,
    cropYear: contract.crop_year || contract.cropYear,
    commissionRate: contract.commission_rate || contract.commissionRate,
    createdAt: contract.created_at || contract.createdAt,
    updatedAt: contract.updated_at || contract.updatedAt,
    _count: {
      shipments: contract.shipments_count || contract._count?.shipments || 0,
    },
  }));
  } catch (error) {
    console.error('Failed to fetch contracts:', error);
    throw new Error('Failed to fetch contracts');
  }
};

const ContractsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [buyerFilter, setBuyerFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all'); // New year filter
  const [cropYearFilter, setCropYearFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);

  // Auto-search with debouncing
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  // Create query key that changes when filters change
  const queryKey = [
    'contracts',
    debouncedSearchText,
    statusFilter,
    supplierFilter,
    buyerFilter,
    yearFilter,
    cropYearFilter,
    dateRange?.[0],
    dateRange?.[1]
  ];
  
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [searchText]);

  // Memoize the shouldFetch calculation to prevent unnecessary re-renders
  const shouldFetch = React.useMemo(() => {
    return hasSearched || debouncedSearchText.length > 0 || 
      statusFilter !== 'all' || supplierFilter !== 'all' || 
      buyerFilter !== 'all' || yearFilter !== 'all' || 
      cropYearFilter !== 'all' || dateRange !== null;
  }, [hasSearched, debouncedSearchText, statusFilter, supplierFilter, buyerFilter, yearFilter, cropYearFilter, dateRange]);

  // Fetch filter values (always loaded)
  const { data: filterData } = useQuery({
    queryKey: ['contract-filters'],
    queryFn: () => contractsApi.getContractFilters(),
    staleTime: 10 * 60 * 1000, // 10 minutes - filter values don't change often
  });

  // Fetch contracts using React Query with dynamic filters
  const { data: contracts = [], isLoading: loading, error } = useQuery({
    queryKey,
    queryFn: () => fetchContracts({
      search: debouncedSearchText || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      supplier: supplierFilter !== 'all' ? supplierFilter : undefined,
      buyer: buyerFilter !== 'all' ? buyerFilter : undefined,
      year: yearFilter !== 'all' ? yearFilter : undefined,
      cropYear: cropYearFilter !== 'all' ? cropYearFilter : undefined,
      dateFrom: dateRange?.[0],
      dateTo: dateRange?.[1],
      limit: pageSize
    }),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Show error message if API call fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load contracts: ${error.message}`);
    }
  }, [error]);

  // Handle filter changes
  const handleFilterChange = () => {
    setHasSearched(true);
    setCurrentPage(1);
  };

  // Get filter values from API data
  const suppliers = filterData?.suppliers || [];
  const buyers = filterData?.buyers || [];
  const years = filterData?.years || [];
  const cropYears = filterData?.cropYears || [];

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'positive';
      case 'COMPLETED': return 'neutral';
      case 'DRAFT': return 'warning';
      case 'CANCELLED': return 'negative';
      default: return 'neutral';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return colors.status.positive;
      case 'COMPLETED': return colors.status.neutral;
      case 'DRAFT': return colors.status.warning;
      case 'CANCELLED': return colors.status.negative;
      default: return colors.status.neutral;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleActionClick = (key: string, record: ContractListItem) => {
    switch (key) {
      case 'view':
        navigate(`/contracts/${record.reference}`);
        break;
      case 'edit':
        navigate(`/contracts/${record.reference}/edit`);
        break;
      case 'duplicate':
        message.info('Duplicate functionality coming soon');
        break;
      case 'delete':
        message.error('Delete functionality requires confirmation');
        break;
      default:
        console.log('Unknown action:', key);
    }
  };

  const getActionMenuItems = (record: ContractListItem): MenuProps['items'] => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: 'View Contract',
    },
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Edit Contract',
      disabled: record.status === 'COMPLETED' || record.status === 'CANCELLED'
    },
    {
      key: 'duplicate',
      icon: <CopyOutlined />,
      label: 'Duplicate Contract',
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete Contract',
      danger: true,
      disabled: record.status === 'ACTIVE' || record._count.shipments > 0
    },
  ];

  const columns: ColumnsType<ContractListItem> = [
    {
      title: 'Contract Reference',
      dataIndex: 'reference',
      key: 'reference',
      fixed: 'left',
      width: 140,
      render: (text: string, record: ContractListItem) => (
        <Button 
          type="link" 
          style={{ padding: 0, fontWeight: 600 }}
          onClick={() => navigate(`/contracts/${record.reference}`)}
        >
          {text}
        </Button>
      ),
      sorter: (a, b) => a.reference.localeCompare(b.reference),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (supplier: ContractListItem['supplier']) => (
        <Tooltip title={supplier.name}>
          <span>{supplier.shortName || supplier.name}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Buyer',
      dataIndex: 'buyer',
      key: 'buyer',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (buyer: ContractListItem['buyer']) => (
        <Tooltip title={buyer.name}>
          <span>{buyer.shortName || buyer.name}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: string) => (
        <StatusPill variant={getStatusVariant(status)}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </StatusPill>
      ),
    },
    {
      title: 'Contract Date',
      dataIndex: 'contractDate',
      key: 'contractDate',
      width: 120,
      render: (date: string) => formatDate(date),
      sorter: (a, b) => new Date(a.contractDate).getTime() - new Date(b.contractDate).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Crop Year',
      dataIndex: 'cropYear',
      key: 'cropYear',
      width: 100,
      align: 'center',
      render: (year: string) => (
        <span style={{ fontFamily: 'Reddit Mono', fontWeight: 500 }}>
          {year}
        </span>
      ),
      sorter: (a, b) => a.cropYear.localeCompare(b.cropYear),
    },
    {
      title: 'Commission Rate',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      width: 120,
      align: 'right',
      render: (rate: number | null) => (
        <span style={{ 
          fontFamily: 'Reddit Mono', 
          fontWeight: 500,
          color: rate && rate > 0 ? colors.status.positive : colors.text.secondary 
        }}>
          {rate && rate > 0 ? `${rate.toFixed(1)}%` : '—'}
        </span>
      ),
      sorter: (a, b) => (a.commissionRate || 0) - (b.commissionRate || 0),
    },
    {
      title: 'Shipments',
      dataIndex: ['_count', 'shipments'],
      key: 'shipmentsCount',
      width: 100,
      align: 'center',
      render: (count: number) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>
          {count}
        </Tag>
      ),
      sorter: (a, b) => a._count.shipments - b._count.shipments,
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      render: (date: string) => formatDate(date),
      sorter: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getActionMenuItems(record),
            onClick: ({ key }) => handleActionClick(key, record),
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            size="small"
          />
        </Dropdown>
      ),
    },
  ];

  // Pagination is now handled inline in the Table component

  return (
    <div style={{
      background: colors.background.primary,
      minHeight: 'calc(100vh - 112px)',
      padding: spacing['2xl']
    }}>
      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: spacing['2xl']
      }}>
        <div>
          <Title>Contracts</Title>
          <p style={{ 
            color: colors.text.secondary,
            fontSize: '16px',
            marginTop: spacing.sm
          }}>
            Manage all your freight contracts and track their progress
          </p>
        </div>
        <Button 
          type="primary" 
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate('/contracts/create')}
        >
          Create Contract
        </Button>
      </div>

      {/* Filters */}
      <CardContainer variant="primary" style={{ marginBottom: spacing['2xl'] }}>
        <div style={{
          display: 'flex',
          gap: spacing.lg,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <Input
            placeholder="Type to search contracts... (e.g., 'CA', 'SKK', 'calif shin', 'ca skk')"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 420 }}
            allowClear
          />
          
          <Select 
            placeholder="All Statuses"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              handleFilterChange();
            }}
            style={{ width: 140 }}
            allowClear
          >
            <Option value="all">All Statuses</Option>
            <Option value="DRAFT">Draft</Option>
            <Option value="ACTIVE">Active</Option>
            <Option value="COMPLETED">Completed</Option>
            <Option value="CANCELLED">Cancelled</Option>
          </Select>

          <Select 
            placeholder="All Suppliers"
            value={supplierFilter}
            onChange={(value) => {
              setSupplierFilter(value);
              handleFilterChange();
            }}
            style={{ width: 160 }}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All Suppliers</Option>
            {suppliers.map((supplier, index) => (
              <Option key={`${supplier.name}-${index}`} value={supplier.displayName || supplier.shortName || supplier.name}>
                {supplier.displayName || supplier.shortName || supplier.name}
              </Option>
            ))}
          </Select>

          <Select 
            placeholder="All Buyers"
            value={buyerFilter}
            onChange={(value) => {
              setBuyerFilter(value);
              handleFilterChange();
            }}
            style={{ width: 160 }}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All Buyers</Option>
            {buyers.map((buyer, index) => (
              <Option key={`${buyer.name}-${index}`} value={buyer.displayName || buyer.shortName || buyer.name}>
                {buyer.displayName || buyer.shortName || buyer.name}
              </Option>
            ))}
          </Select>

          <Select 
            placeholder="All Years"
            value={yearFilter}
            onChange={(value) => {
              setYearFilter(value);
              handleFilterChange();
            }}
            style={{ width: 100 }}
            allowClear
          >
            <Option value="all">All Years</Option>
            {years.map(year => (
              <Option key={year} value={year}>{year}</Option>
            ))}
          </Select>

          <Select 
            placeholder="All Crop Years"
            value={cropYearFilter}
            onChange={(value) => {
              setCropYearFilter(value);
              handleFilterChange();
            }}
            style={{ width: 130 }}
            allowClear
          >
            <Option value="all">All Crop Years</Option>
            {cropYears.map(year => (
              <Option key={year} value={year}>{year}</Option>
            ))}
          </Select>

          <RangePicker
            placeholder={['Start Date', 'End Date']}
            onChange={(dates) => {
              if (dates) {
                setDateRange([dates[0]?.format('YYYY-MM-DD') || '', dates[1]?.format('YYYY-MM-DD') || '']);
              } else {
                setDateRange(null);
              }
              handleFilterChange();
            }}
          />

          <Button 
            icon={<FilterOutlined />}
            onClick={() => {
              setSearchText('');
              setStatusFilter('all');
              setSupplierFilter('all');
              setBuyerFilter('all');
              setYearFilter('all');
              setCropYearFilter('all');
              setDateRange(null);
              setHasSearched(false);
            }}
          >
            Clear All
          </Button>
        </div>
      </CardContainer>

      {/* Contracts Table or Empty State */}
      <CardContainer variant="secondary">
        {!shouldFetch ? (
          <div style={{
            textAlign: 'center',
            padding: `${spacing['3xl']} ${spacing.xl}`,
            color: colors.text.secondary
          }}>
            <SearchOutlined style={{ fontSize: '48px', marginBottom: spacing.lg }} />
            <h3 style={{ 
              color: colors.text.primary, 
              marginBottom: spacing.sm,
              fontSize: '18px',
              fontWeight: 600
            }}>
              Search for Contracts
            </h3>
            <p style={{ fontSize: '16px', marginBottom: spacing.lg }}>
              Enter a contract reference, supplier name, or buyer name to find contracts.<br />
              You can also filter by status, crop year, or date range.
            </p>
            <p style={{ fontSize: '14px', color: colors.text.tertiary }}>
              📊 Total contracts in system: {loading ? 'Loading...' : '1,658+'}
            </p>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={contracts}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contracts`,
            }}
            scroll={{ x: 1400 }}
            size="middle"
            style={{
              '& .ant-table-thead > tr > th': {
                backgroundColor: colors.background.tertiary,
                fontWeight: 600,
              }
            } as any}
          />
        )}
      </CardContainer>
    </div>
  );
};

export default ContractsList;